import { Dynamic } from "solid-js/web";
import { cn } from "@tendly/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but the
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth card
   * title owns the page heading for a11y).
   */
  as?: "h1" | "p";
}

/**
 * Tendly's own mark — does NOT use the CheckSquare inherited from the template.
 *
 * Concept: a wrench (maintenance, "keeping everything ready") whose head
 * embraces a point/bolt — the asset you care for. It is Tendly's distinctive
 * symbol = asset management and maintenance.
 *
 * Contrast (real gotcha that broke GymLog/bso): the mark is NEVER painted with
 * `text-accent` / gradient over a faint surface (`bg-accent/10`), because
 * it fails AA in themes such as Carbon or Royal Velvet (low-contrast accent).
 * Instead the mark ALWAYS goes over a SOLID `bg-accent` chip with
 * `text-accent-foreground` (paired with guaranteed contrast by the seed: every
 * theme without accent_foreground inherits #fff). The wordmark uses
 * `text-foreground` and the submark is distinguished by WEIGHT, not color.
 */
function TendlyMark(props: { class?: string }) {
  return (
    <svg
      class={props.class}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/*
        Llave inglesa con un perno "ajustado" como hueco en el mango
        (fill-rule evenodd → el círculo queda en negativo, mostrando el chip
        sólido detrás; no introduce un color propio, así NO rompe el contraste).
      */}
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M16.9 5.1a3.8 3.8 0 0 0-4.62 4.62L5.6 16.4a1.9 1.9 0 0 0 2.7 2.7l6.68-6.68a3.8 3.8 0 0 0 4.62-4.62l-2.1 2.1a1.5 1.5 0 0 1-2.1-2.1l2.1-2.1ZM7.45 15.4a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5">
            <TendlyMark class="h-5 w-5" />
          </div>
          <span class="font-display text-xl font-bold tracking-tight text-foreground">Tendly</span>
        </>
      ) : (
        <>
          <div class="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform duration-300 hover:scale-105 active:scale-95">
            <TendlyMark class="h-9 w-9" />
          </div>
          <Dynamic
            component={wordmarkTag()}
            class="font-display text-3xl font-bold tracking-tight text-foreground"
          >
            Tendly
          </Dynamic>
        </>
      )}
    </div>
  );
}
